import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';

import { closeModal } from 'store/modals/actions';
import { MODAL_WORKSPACE_GROUPING_CONFIRM } from 'store/modals/modals';
import selectModal from 'store/modals/selectors';

import ModalWrapper from 'Common/Components/ModalWrapper';
import {
  ModalTitle, Title, BodyText, ModalContent, ButtonsContainer,
  ConfirmButton, CancelButton, Warning, ModalBody
} from 'Common/Components/ModalConfirmation/styled';

import { selectWsDetail } from 'store/Workspace/selectors';
import { selectPendingWorkspaceUpdate } from 'store/WorkspaceEditCreate/selectors';

import { updateWS, setPendingWorkspaceUpdate } from 'Screens/Contextualization/Workspaces/actions/Actions';
import {
  enqueueCommand,
  removeCommand,
  setErrorInCommand,
  showProcessingQueue,
  expandProcessingQueue,
  MARK_COMMAND_FINISHED
} from 'Common/Components/FileUploadContext/actions';
import { extractIsGroupingFromResponse } from 'Common/Functions/vulnerabilityNameEdit';
import api from 'services/api';
import { getVulnsGeneral, toApiUtcDate } from 'store/ManageGeneral/actions';

const DEFAULT_GROUP_ALGORITHM = 'levenshtein';
const DEFAULT_GROUP_THRESHOLD = 80;
const GROUP_BY_LABELS = {
  name: 'Name',
  cve: 'CVE',
  none: 'None'
};

const normalizeGroupByValue = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized || normalized === 'none' || normalized === 'null') return 'none';
  if (normalized === 'name' || normalized === 'cve') return normalized;
  return 'none';
};

const normalizeThreshold = (value, fallback) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return fallback;
  return parsed;
};

const resolveNextGroupByValue = (workspace = {}, payload = {}) => {
  const currentGroupBy = normalizeGroupByValue(get(workspace, 'group_by'));
  if (Object.prototype.hasOwnProperty.call(payload, 'group_by')) {
    return normalizeGroupByValue(payload.group_by);
  }
  return currentGroupBy;
};

const resolveWorkspaceGroupingCommandType = (workspace = {}, payload = {}) => {
  const currentGroupBy = normalizeGroupByValue(get(workspace, 'group_by'));
  const nextGroupBy = resolveNextGroupByValue(workspace, payload);

  if (currentGroupBy === 'none' && nextGroupBy !== 'none') return 'grouping';
  if (currentGroupBy !== 'none' && nextGroupBy === 'none') return 'ungrouping';
  if (currentGroupBy !== nextGroupBy) return 'regrouping';

  const currentAlgorithm = String(get(workspace, 'group_algorithm', DEFAULT_GROUP_ALGORITHM) || DEFAULT_GROUP_ALGORITHM)
    .trim()
    .toLowerCase();
  const nextAlgorithm = String(payload.group_algorithm ?? currentAlgorithm)
    .trim()
    .toLowerCase();

  const currentThreshold = normalizeThreshold(
    get(workspace, 'group_threshold', DEFAULT_GROUP_THRESHOLD),
    DEFAULT_GROUP_THRESHOLD
  );
  const nextThreshold = normalizeThreshold(payload.group_threshold, currentThreshold);

  if (nextGroupBy === 'name' && (currentAlgorithm !== nextAlgorithm || currentThreshold !== nextThreshold)) {
    return 'regrouping';
  }

  return 'grouping';
};

const buildModalCopy = (commandType, nextGroupBy) => {
  const criterion = GROUP_BY_LABELS[nextGroupBy] || String(nextGroupBy || '').toUpperCase();

  if (commandType === 'grouping') {
    return {
      title: 'Vulnerability Grouping',
      firstLine: `Vulnerabilities will be grouped by ${criterion}.`,
      secondLine: 'This operation might take a few minutes. Continue?'
    };
  }

  if (commandType === 'ungrouping') {
    return {
      title: 'Vulnerability Ungrouping',
      firstLine: 'Current groups will be lost.',
      secondLine: 'This operation might take a few minutes. Continue?'
    };
  }

  return {
    title: 'Vulnerability Regrouping',
    firstLine: 'Current groups will be rebuilt with the new criteria.',
    secondLine: 'This operation might take a few minutes. Continue?'
  };
};

const WorkspaceGroupingConfirmModal = () => {
  const dispatch = useDispatch();

  const show = useSelector((state) => selectModal(state, MODAL_WORKSPACE_GROUPING_CONFIRM));
  const wsSelected = useSelector(selectWsDetail);
  const pendingWorkspaceUpdate = useSelector(selectPendingWorkspaceUpdate);

  const wsName = get(wsSelected, 'name', '');
  const payloadToApply = pendingWorkspaceUpdate || {};
  const commandType = resolveWorkspaceGroupingCommandType(wsSelected, payloadToApply);
  const nextGroupBy = resolveNextGroupByValue(wsSelected, payloadToApply);
  const modalCopy = buildModalCopy(commandType, nextGroupBy);

  const onClose = () => {
    dispatch(closeModal(MODAL_WORKSPACE_GROUPING_CONFIRM));
    dispatch(setPendingWorkspaceUpdate(null));
  };

  const onSubmit = async () => {
    const mockId = `grouping_${Date.now()}`;

    try {
      dispatch(closeModal(MODAL_WORKSPACE_GROUPING_CONFIRM));

      dispatch(showProcessingQueue(true));
      dispatch(expandProcessingQueue(true));

      dispatch(enqueueCommand({
        mockCommandId: mockId,
        commandType,
        name: wsName,
        fileType: 'grouping',
        isGrouping: true,
        error: false,
        finished: false
      }));

      dispatch(setPendingWorkspaceUpdate(null));

      const res = await dispatch(updateWS(wsName, payloadToApply));

      const isGroupingFromResponse = extractIsGroupingFromResponse(res);
      if (isGroupingFromResponse === true) {
        const groupingCommandDateUTCString = toApiUtcDate(new Date());
        try {
          await api.workspace.getLastGroupingCommand(wsName, groupingCommandDateUTCString);
          dispatch(getVulnsGeneral(wsName));
        } catch (_error) {
          // ignore last grouping resolution failures here
        }
      }

      const commandId =
        res?.command_id ||
        res?.commandId ||
        res?.metadata?.command_id ||
        res?.data?.command_id;

      if (commandId) {
        dispatch(removeCommand(null, mockId));
        dispatch(enqueueCommand({
          command_id: commandId,
          commandType,
          name: wsName,
          fileType: 'grouping',
          isGrouping: true,
          groupingCommandResolved: false,
          error: false,
          finished: false
        }));
        return;
      }

      if (!commandId) {
        dispatch({
          type: MARK_COMMAND_FINISHED,
          command: {
            mockCommandId: mockId,
            groupingCommandResolved: true,
            finished: true
          }
        });
      }
    } catch (e) {
      dispatch(setErrorInCommand?.(null, mockId, wsName, 'Grouping request failed.'));
    }
  };

  if (!show) return null;

  return (
    <ModalWrapper>
      <ModalContent>
        <ModalTitle>
          <Warning />
          <Title>{ modalCopy.title }</Title>
        </ModalTitle>

        <ModalBody>
          <BodyText>{ modalCopy.firstLine }</BodyText>
          <BodyText>{ modalCopy.secondLine }</BodyText>
        </ModalBody>

        <ButtonsContainer>
          <CancelButton onClick={ onClose }>Cancel</CancelButton>
          <ConfirmButton onClick={ onSubmit }>Ok</ConfirmButton>
        </ButtonsContainer>
      </ModalContent>
    </ModalWrapper>
  );
};

export default WorkspaceGroupingConfirmModal;
